import React, { useState, useCallback, useMemo, memo } from "react";
import { Input, InputGroup, InputLeftElement, IconButton, Box } from "@chakra-ui/react";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useDebounce } from "hooks/useDebouce";

const SearchByItem = memo(({ 
  onSearch, 
  placeholder = "Buscar por número de item (ej: 2)...", 
  isLoading = false,
  clearSearch,
  searchValue = ""
}) => {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  
  // Debounce para evitar búsquedas excesivas (300ms de delay)
  const debouncedSearchValue = useDebounce(localSearchValue, 300);

  // Memoizar el valor de búsqueda para evitar re-renders innecesarios
  const memoizedSearchValue = useMemo(() => debouncedSearchValue, [debouncedSearchValue]);

  // Ejecutar búsqueda cuando el valor debounced cambie
  React.useEffect(() => {
    onSearch(memoizedSearchValue);
  }, [memoizedSearchValue, onSearch]);

  const handleInputChange = useCallback((e) => {
    setLocalSearchValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setLocalSearchValue("");
    onSearch("");
    if (clearSearch) {
      clearSearch();
    }
  }, [onSearch, clearSearch]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      // Ejecutar búsqueda inmediatamente al presionar Enter
      onSearch(e.target.value);
    }
  }, [onSearch]);

  return (
    <Box mb={4}>
      <InputGroup size="md">
        <InputLeftElement pointerEvents="none">
          <FaSearch color="gray.300" />
        </InputLeftElement>
        <Input
          placeholder={placeholder}
          value={localSearchValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          bg="white"
          border="1px solid"
          borderColor="gray.300"
          _hover={{
            borderColor: "blue.400",
          }}
          _focus={{
            borderColor: "blue.500",
            boxShadow: "0 0 0 1px blue.500",
          }}
        />
        {localSearchValue && (
          <IconButton
            aria-label="Limpiar búsqueda"
            icon={<FaTimes />}
            size="sm"
            onClick={handleClear}
            position="absolute"
            right="8px"
            top="50%"
            transform="translateY(-50%)"
            bg="transparent"
            _hover={{ bg: "gray.100" }}
            zIndex={1}
          />
        )}
      </InputGroup>
    </Box>
  );
});

SearchByItem.displayName = 'SearchByItem';

export default SearchByItem;
